// src/components/CustomerSummary/components/StudiesStartedWorkflowDistributionTable.jsx
// ============================================================================
// BLOCK CS.UI.WD.1 — StudiesStartedWorkflowDistributionTable
// ----------------------------------------------------------------------------
// PURPOSE
// - Displays "Studies Started Workflow Distribution" as a 3-column table
//   matching the StudiesStartedTable layout.
// - Read-only: values are derived upstream (no editing, no lock).
//
// EXPECTED TEXT FORMAT (tab-separated lines)
// - Header lines may include:
//     Facility: ...
//     Date Range: YYYY-MM-DD -> YYYY-MM-DD
// - Section header line:
//     Studies Started Workflow Distribution
// - Data lines:
//     Label \t Workflow \t Percent
// ============================================================================

import React from 'react';

import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';

// ============================================================================
// BLOCK CS.UI.WD.1.1 — Parsing helpers
// ============================================================================
function safeStr(v) {
  return v === null || v === undefined ? '' : String(v);
}

function isHeaderLine(line) {
  const s = safeStr(line).trim();
  return s.startsWith('Facility:') || s.startsWith('Date Range:');
}

function parseWorkflowDistribution(text) {
  const raw = safeStr(text);
  const lines = raw.split(/\r?\n/);

  const headerLines = lines.filter((l) => isHeaderLine(l)).map((l) => safeStr(l).trim());

  // Find section marker; if not found, treat everything as data.
  const idx = lines.findIndex((l) =>
    safeStr(l).toLowerCase().includes('studies started workflow distribution')
  );

  const dataLines = idx >= 0 ? lines.slice(idx + 1) : lines;

  const rows = dataLines
    .map((l) => safeStr(l).trim())
    .filter((l) => l.length > 0)
    .map((l) => l.split('\t').map((x) => safeStr(x).trim()))
    .filter((parts) => parts.length >= 3)
    .map((parts) => ({
      label: parts[0] || '',
      workflow: parts[1] || '',
      value: parts[2] || '',
    }));

  return { headerLines, rows };
}

function rowStyle(label) {
  const l = safeStr(label).trim().toLowerCase();
  const isGrand = false; // No grand total row expected here (only workflow totals)
  const isSubtotal = l === 'total';
  return { isGrand, isSubtotal };
}

// ============================================================================
// BLOCK CS.UI.WD.2 — Component
// ============================================================================
const DEFAULT_WIDTHS = {
  col1: 360,
  col2: 280,
  col3: 160,
};

function clamp(n, min, max) {
  return Math.max(min, Math.min(max, n));
}

function parseWidths(input) {
  const col1 = Number(input?.col1);
  const col2 = Number(input?.col2);
  const col3 = Number(input?.col3);

  const ok =
    Number.isFinite(col1) &&
    Number.isFinite(col2) &&
    Number.isFinite(col3) &&
    col1 > 0 &&
    col2 > 0 &&
    col3 > 0;

  return ok ? { col1, col2, col3 } : null;
}

function ColumnDivider({ onMouseDown }) {
  return (
    <Box
      onMouseDown={onMouseDown}
      sx={{
        position: 'absolute',
        top: 0,
        right: -6,
        width: 12,
        height: '100%',
        cursor: 'col-resize',
        zIndex: 5,
        '&::after': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: '50%',
          transform: 'translateX(-50%)',
          width: '1px',
          height: '100%',
          backgroundColor: 'rgba(0,0,0,0.18)',
        },
        '&:hover::after': {
          backgroundColor: 'rgba(0,0,0,0.35)',
        },
      }}
    />
  );
}

function useColumnResize({ widths, setWidths, onPersistWidths }) {
  const dragRef = React.useRef(null);

  const startDrag = React.useCallback(
    (which, e) => {
      e.preventDefault();
      e.stopPropagation();

      dragRef.current = { which, startX: e.clientX, start: { ...widths } };

      const onMove = (ev) => {
        const drag = dragRef.current;
        if (!drag) {
          return;
        }

        const dx = ev.clientX - drag.startX;
        const minCol = 140;
        const maxCol = 1200;

        if (drag.which === 'c1') {
          const total = drag.start.col1 + drag.start.col2;
          const nextCol1 = clamp(drag.start.col1 + dx, minCol, maxCol);
          const nextCol2 = clamp(total - nextCol1, minCol, maxCol);

          const fixedTotal = nextCol1 + nextCol2;
          const finalCol1 = clamp(nextCol1 + (total - fixedTotal), minCol, maxCol);

          setWidths((p) => ({ ...p, col1: finalCol1, col2: total - finalCol1 }));
        }

        if (drag.which === 'c2') {
          const total = drag.start.col2 + drag.start.col3;
          const nextCol2 = clamp(drag.start.col2 + dx, minCol, maxCol);
          const nextCol3 = clamp(total - nextCol2, minCol, maxCol);

          const fixedTotal = nextCol2 + nextCol3;
          const finalCol2 = clamp(nextCol2 + (total - fixedTotal), minCol, maxCol);

          setWidths((p) => ({ ...p, col2: finalCol2, col3: total - finalCol2 }));
        }

        if (drag.which === 'c3') {
          const nextCol3 = clamp(drag.start.col3 + dx, minCol, maxCol);
          setWidths((p) => ({ ...p, col3: nextCol3 }));
        }
      };

      const onUp = () => {
        dragRef.current = null;
        window.removeEventListener('mousemove', onMove);
        window.removeEventListener('mouseup', onUp);
        onPersistWidths();
      };

      window.addEventListener('mousemove', onMove);
      window.addEventListener('mouseup', onUp);
    },
    [onPersistWidths, setWidths, widths]
  );

  return { startDrag };
}

export default function StudiesStartedWorkflowDistributionTable({
  text,
  columnWidths,
  onColumnWidthsChange,
}) {
  const parsed = React.useMemo(() => parseWorkflowDistribution(text), [text]);
  const { headerLines, rows } = parsed;

  const initialWidths = React.useMemo(() => {
    const fromProp = parseWidths(columnWidths);
    if (fromProp) {
      return fromProp;
    }

    return { ...DEFAULT_WIDTHS };
  }, [columnWidths]);

  const [widths, setWidths] = React.useState(initialWidths);
  const widthsRef = React.useRef(widths);

  React.useEffect(() => {
    widthsRef.current = widths;
  }, [widths]);

  React.useEffect(() => {
    const fromProp = parseWidths(columnWidths);
    if (!fromProp) {
      return;
    }

    setWidths((prev) => {
      const same =
        Number(prev.col1) === Number(fromProp.col1) &&
        Number(prev.col2) === Number(fromProp.col2) &&
        Number(prev.col3) === Number(fromProp.col3);
      return same ? prev : fromProp;
    });
  }, [columnWidths]);

  const persistWidths = React.useCallback(() => {
    if (typeof onColumnWidthsChange === 'function') {
      onColumnWidthsChange({ ...widthsRef.current });
    }
  }, [onColumnWidthsChange]);

  const { startDrag } = useColumnResize({ widths, setWidths, onPersistWidths: persistWidths });

  const onDividerC1MouseDown = React.useCallback((e) => startDrag('c1', e), [startDrag]);
  const onDividerC2MouseDown = React.useCallback((e) => startDrag('c2', e), [startDrag]);
  const onDividerC3MouseDown = React.useCallback((e) => startDrag('c3', e), [startDrag]);

  const totalWidth = widths.col1 + widths.col2 + widths.col3;

  return (
    <Box>
      {headerLines.length > 0 && (
        <Box sx={{ mb: 1 }}>
          {headerLines.map((l) => (
            <Typography key={l} variant="body2" sx={{ lineHeight: 1.2 }}>
              {l}
            </Typography>
          ))}
        </Box>
      )}

      <TableContainer component={Paper} variant="outlined" sx={{ overflowX: 'auto' }}>
        <Table size="small" sx={{ tableLayout: 'fixed', width: totalWidth, minWidth: totalWidth }}>
          <colgroup>
            <col style={{ width: `${widths.col1}px` }} />
            <col style={{ width: `${widths.col2}px` }} />
            <col style={{ width: `${widths.col3}px` }} />
          </colgroup>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 800, width: `${widths.col1}px`, position: 'relative' }}>
                Study Type
                <ColumnDivider onMouseDown={onDividerC1MouseDown} />
              </TableCell>
              <TableCell sx={{ fontWeight: 800, width: `${widths.col2}px`, position: 'relative' }}>
                Workflow
                <ColumnDivider onMouseDown={onDividerC2MouseDown} />
              </TableCell>
              <TableCell
                align="right"
                sx={{
                  fontWeight: 800,
                  width: `${widths.col3}px`,
                  position: 'relative',
                }}
              >
                Percent
                <ColumnDivider onMouseDown={onDividerC3MouseDown} />
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {rows.map((r) => {
              const { isGrand, isSubtotal } = rowStyle(r.label);

              return (
                <TableRow
                  key={`${r.label}-${r.workflow}`}
                  sx={{
                    backgroundColor: isGrand ? 'rgba(255, 235, 59, 0.20)' : 'transparent',
                  }}
                >
                  <TableCell
                    sx={{
                      fontWeight: isGrand || isSubtotal ? 800 : 400,
                      borderTop: isSubtotal ? '1px solid rgba(0,0,0,0.35)' : undefined,
                    }}
                  >
                    {r.label}
                  </TableCell>

                  <TableCell
                    sx={{
                      fontWeight: isGrand || isSubtotal ? 800 : 400,
                      borderTop: isSubtotal ? '1px solid rgba(0,0,0,0.35)' : undefined,
                    }}
                  >
                    {r.workflow}
                  </TableCell>

                  <TableCell
                    align="right"
                    sx={{
                      fontWeight: isGrand || isSubtotal ? 800 : 400,
                      borderTop: isSubtotal ? '1px solid rgba(0,0,0,0.35)' : undefined,
                      fontVariantNumeric: 'tabular-nums',
                    }}
                  >
                    {r.value}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
